import { useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { configs } from "../configs";
import Login from "../features/auth/login";
import { getDashboardRedirectPath } from "../helpers/getDashboardRedirectPath";
import AuthWrapper from "./AuthWrapper";
import DirectRouter from "./DirectRouter";

/**
 * AppRouter handles the top-level routing for the application.
 * - If user is not logged in, shows the login page at /:lang/admin/login
 * - If user is logged in, shows the AuthWrapper which fetches user data
 *   and then renders the DirectRouter with protected routes
 */
const AppRouter = () => {
  const location = useLocation()
  const isLoggedIn = useSelector((state) => state.app?.auth?.isLoggedIn)
  const defaultLang = configs.languages[0]?.key || "en"
  const user = useSelector((state) => state.app?.user?.info?.user ?? state.app?.user?.info)

  // Check if current path is a login page (with or without language prefix)
  const isLoginPage = location.pathname.includes("/admin/login");

  // Get language from URL or use default
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const langFromPath = pathSegments[0];
  const isValidLang = configs.languages.some(l => l.key === langFromPath);
  const currentLang = isValidLang ? langFromPath : defaultLang;

  // Helper to get localized login path
  const getLoginPath = (lang = currentLang) => `/${lang}/admin/login`;

  // If not logged in and not on login page, redirect to login with language
  if (!isLoggedIn && !isLoginPage) {
    return <Navigate to={getLoginPath()} replace />;
  }

  // If logged in and on login page, redirect to dashboard with language based on user's app access
  if (isLoggedIn && isLoginPage) {
    const dashboardPath = getDashboardRedirectPath(user)
    return <Navigate to={dashboardPath} replace />
  }

  return (
    <Routes>
      {/* Login Route - Public with language prefix */}
      <Route
        path="/:lang/admin/login"
        element={
          isLoggedIn ? (
            <Navigate to={getDashboardRedirectPath(user)} replace />
          ) : (
            <Login />
          )
        }
      />

      {/* Legacy login route without language - redirect to default language */}
      <Route
        path="/admin/login"
        element={<Navigate to={getLoginPath(defaultLang)} replace />}
      />

      {/* All other routes - Protected */}
      <Route
        path="/*"
        element={
          isLoggedIn ? (
            <AuthWrapper>
              <DirectRouter />
            </AuthWrapper>
          ) : (
            <Navigate to={getLoginPath()} replace />
          )
        }
      />
    </Routes>
  )
};

export default AppRouter;
